import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

// Rapor dönem seçici: hazır aralık çipleri + özel tarih aralığı + opsiyonel
// karşılaştırma dönemi. Değer sözleşmesi:
// { preset, from: 'yyyy-MM-dd', to: 'yyyy-MM-dd', compareFrom?, compareTo? }

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.button`
  border: 1.5px solid ${({ $active, theme }) => ($active ? theme.colors.primary.main : '#E0E4E8')};
  background: ${({ $active }) => ($active ? 'rgba(2, 145, 239, 0.08)' : '#fff')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary.main : '#52514e')};
  border-radius: 14px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
`;

const DatesRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #6B7280;

  input {
    border: 1.5px solid #E0E4E8;
    border-radius: 4px;
    padding: 5px 8px;
    font-size: 12px;
    font-family: inherit;
    color: inherit;
  }
`;

const CompareToggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: #52514e;
  cursor: pointer;
`;

const iso = (d) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

export function presetRange(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  switch (preset) {
    case 'thisWeek': {
      const dow = (now.getDay() + 6) % 7; // Pzt=0
      const start = new Date(y, m, now.getDate() - dow);
      return { from: iso(start), to: iso(now) };
    }
    case 'thisQuarter': {
      const qStart = new Date(y, Math.floor(m / 3) * 3, 1);
      return { from: iso(qStart), to: iso(now) };
    }
    case 'thisYear':
      return { from: iso(new Date(y, 0, 1)), to: iso(now) };
    case 'lastMonth': {
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0);
      return { from: iso(start), to: iso(end) };
    }
    case 'lastQuarter': {
      const qStartMonth = Math.floor(m / 3) * 3 - 3;
      const start = new Date(y, qStartMonth, 1);
      const end = new Date(y, qStartMonth + 3, 0);
      return { from: iso(start), to: iso(end) };
    }
    case 'thisMonth':
    default:
      return { from: iso(new Date(y, m, 1)), to: iso(now) };
  }
}

export default function PeriodRangePicker({ value, onChange }) {
  const { t } = useTranslation();

  const presets = useMemo(() => ([
    { key: 'thisWeek', label: t('periodPicker.thisWeek') },
    { key: 'thisMonth', label: t('periodPicker.thisMonth') },
    { key: 'thisQuarter', label: t('periodPicker.thisQuarter') },
    { key: 'thisYear', label: t('periodPicker.thisYear') },
    { key: 'lastMonth', label: t('periodPicker.lastMonth') },
    { key: 'lastQuarter', label: t('periodPicker.lastQuarter') },
    { key: 'custom', label: t('periodPicker.custom') },
  ]), [t]);

  const applyPreset = (key) => {
    if (key === 'custom') {
      onChange({ ...value, preset: 'custom' });
      return;
    }
    onChange({ ...value, preset: key, ...presetRange(key) });
  };

  const compareOn = value.compareFrom != null || value.compareTo != null;

  return (
    <Wrap>
      <Chips>
        {presets.map((p) => (
          <Chip key={p.key} type="button" $active={value.preset === p.key} onClick={() => applyPreset(p.key)}>
            {p.label}
          </Chip>
        ))}
      </Chips>
      <DatesRow>
        <span>{t('periodPicker.range')}:</span>
        <input
          type="date"
          value={value.from || ''}
          onChange={(e) => onChange({ ...value, preset: 'custom', from: e.target.value })}
        />
        —
        <input
          type="date"
          value={value.to || ''}
          onChange={(e) => onChange({ ...value, preset: 'custom', to: e.target.value })}
        />
        <CompareToggle>
          <input
            type="checkbox"
            checked={compareOn}
            onChange={(e) => {
              if (e.target.checked) {
                onChange({ ...value, compareFrom: value.compareFrom || '', compareTo: value.compareTo || '' });
              } else {
                const next = { ...value };
                delete next.compareFrom;
                delete next.compareTo;
                onChange(next);
              }
            }}
          />
          {t('periodPicker.compare')}
        </CompareToggle>
        {compareOn && (
          <>
            <input
              type="date"
              value={value.compareFrom || ''}
              onChange={(e) => onChange({ ...value, compareFrom: e.target.value })}
            />
            —
            <input
              type="date"
              value={value.compareTo || ''}
              onChange={(e) => onChange({ ...value, compareTo: e.target.value })}
            />
          </>
        )}
      </DatesRow>
    </Wrap>
  );
}
